import { create } from "zustand";
import { devtools } from "zustand/middleware";
import type {
  BikeMarker,
  MapBounds,
  HeatmapData,
  BikeRoute,
  Geofence,
} from "../types/map";
import { apiService } from "../services/api";
import { MAP_CONFIG, UNCATEGORIZED_FILTER_VALUE } from "../utils/constants";

export interface MapState {
  // Map data
  /**
   * @unused Vehicle lists come with their location data, so this variable is no longer needed.
   */
  bikeMarkers: BikeMarker[];
  selectedRoute: BikeRoute | null;
  heatmapData: HeatmapData | null;
  geofences: Geofence[];

  // Map settings
  bounds: MapBounds | null;
  center: [number, number];
  zoom: number;
  showHeatmap: boolean;
  showGeofences: boolean;

  // Filters
  statusFilter: "" | "active" | "inactive" | "maintenance";
  showMovingOnly: boolean;
  categoryFilters: string[];

  // Loading states
  loadingMarkers: boolean;
  loadingRoute: boolean;
  loadingHeatmap: boolean;

  // Actions
  /**
   * @unused Vehicle lists come with their location data, so this method is no longer needed.
   */
  fetchBikeMarkers: (bounds?: MapBounds) => Promise<void>;
  fetchBikeRoute: (
    bikeId: string,
    params?: Record<string, unknown>,
  ) => Promise<void>;
  fetchHeatmapData: (
    bounds: MapBounds,
    params?: Record<string, unknown>,
  ) => Promise<void>;
  fetchGeofences: () => Promise<void>;

  setBounds: (bounds: MapBounds) => void;
  setCenter: (center: [number, number]) => void;
  setZoom: (zoom: number) => void;
  setShowHeatmap: (show: boolean) => void;
  setShowGeofences: (show: boolean) => void;
  setStatusFilter: (filter: MapState["statusFilter"]) => void;
  setShowMovingOnly: (show: boolean) => void;
  setCategoryFilters: (categoryIds: string[]) => void;

  clearSelectedRoute: () => void;
  /**
   * @unused Vehicle lists come with their location data, so this method is no longer needed.
   */
  updateBikeStatus: (data: BikeMarker) => void;
}

export const useMapStore = create<MapState>()(
  devtools(
    (set, get) => ({
      // Initial state
      bikeMarkers: [],
      selectedRoute: null,
      heatmapData: null,
      geofences: [],
      bounds: null,
      center: MAP_CONFIG.DEFAULT_CENTER,
      zoom: MAP_CONFIG.DEFAULT_ZOOM,
      showHeatmap: false,
      showGeofences: true,
      statusFilter: "",
      showMovingOnly: false,
      categoryFilters: [],
      loadingMarkers: false,
      loadingRoute: false,
      loadingHeatmap: false,

      // Actions

      /**
       * @unused Vehicle lists come with their location data, so this method is no longer needed.
       */
      fetchBikeMarkers: async (bounds) => {
        set({ loadingMarkers: true });
        try {
          const { categoryFilters } = get();
          const selectedCategoryIds = categoryFilters.filter(
            (categoryId) => categoryId !== UNCATEGORIZED_FILTER_VALUE,
          );
          const includeUncategorized = categoryFilters.includes(
            UNCATEGORIZED_FILTER_VALUE,
          );

          const params: Record<string, unknown> = bounds
            ? {
                north: bounds.north,
                south: bounds.south,
                east: bounds.east,
                west: bounds.west,
                include_inactive: true,
              }
            : { include_inactive: true };

          if (selectedCategoryIds.length > 0) {
            params.category_ids = selectedCategoryIds.join(",");
          }

          if (includeUncategorized) {
            params.include_uncategorized = true;
          }

          const response = (await apiService.getBikeMarkers(params)) as {
            markers: BikeMarker[];
          };
          const markers = response.markers;

          const { bikeMarkers } = get();
          const merged = [
            ...bikeMarkers.filter(
              (existing) =>
                !markers.some((m) => m.bike_id === existing.bike_id),
            ),
            ...markers,
          ];

          set({ bikeMarkers: merged, loadingMarkers: false });
        } catch (error) {
          console.error("Error fetching bike markers:", error);
          set({ loadingMarkers: false });
        }
      },

      /**
       * @unused
       */
      fetchBikeRoute: async (bikeId, params = {}) => {
        set({ loadingRoute: true });
        try {
          const route = (await apiService.getMapRoute(bikeId, {
            zoom_level: get().zoom,
            max_points: 1000,
            ...params,
          })) as BikeRoute;
          set({ selectedRoute: route, loadingRoute: false });
        } catch (error) {
          console.error("Error fetching bike route:", error);
          set({ loadingRoute: false });
        }
      },

      fetchHeatmapData: async (bounds, params = {}) => {
        set({ loadingHeatmap: true });
        try {
          const heatmap = (await apiService.getHeatmapData({
            ...bounds,
            grid_size: 0.001,
            ...params,
          })) as HeatmapData;
          set({ heatmapData: heatmap, loadingHeatmap: false });
        } catch (error) {
          console.error("Error fetching heatmap data:", error);
          set({ loadingHeatmap: false });
        }
      },

      fetchGeofences: async () => {
        try {
          const response = (await apiService.getGeofences()) as {
            geofences: Geofence[];
          };
          set({ geofences: response.geofences || response });
        } catch (error) {
          console.error("Error fetching geofences:", error);
        }
      },

      setBounds: (bounds) => {
        const currentBounds = get().bounds;
        // Only update if bounds actually changed to prevent infinite loops
        if (
          !currentBounds ||
          Math.abs(currentBounds.north - bounds.north) > 0.001 ||
          Math.abs(currentBounds.south - bounds.south) > 0.001 ||
          Math.abs(currentBounds.east - bounds.east) > 0.001 ||
          Math.abs(currentBounds.west - bounds.west) > 0.001
        ) {
          set({ bounds });
        }
      },
      setCenter: (center) => {
        const currentCenter = get().center;
        // Only update if center actually changed
        if (
          !currentCenter ||
          Math.abs(currentCenter[0] - center[0]) > 0.0001 ||
          Math.abs(currentCenter[1] - center[1]) > 0.0001
        ) {
          set({ center });
        }
      },
      setZoom: (zoom) => {
        const currentZoom = get().zoom;
        // Only update if zoom actually changed
        if (currentZoom !== zoom) {
          set({ zoom });
        }
      },
      setShowHeatmap: (show) => set({ showHeatmap: show }),
      setShowGeofences: (show) => set({ showGeofences: show }),

      setStatusFilter: (filter) => {
        set({ statusFilter: filter });
      },

      setShowMovingOnly: (show) => {
        set({ showMovingOnly: show });
      },

      setCategoryFilters: (categoryIds) => {
        set({ categoryFilters: categoryIds });
      },

      clearSelectedRoute: () => set({ selectedRoute: null }),

      /**
       * @unused Vehicle lists come with their location data, so this method is no longer needed.
       */
      updateBikeStatus: (data: BikeMarker) => {
        const { bikeMarkers } = get();
        const updatedMarkers = bikeMarkers.map((marker) =>
          marker.bike_id === data.bike_id ? { ...marker, ...data } : marker,
        );
        set({ bikeMarkers: updatedMarkers });
      },
    }),
    { name: "map-store" },
  ),
);
